const API_BASE = (process.env.PUBLIC_URL || '') + '/api';

async function request(path, options = {}) {
  const res = await fetch(API_BASE + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(err.detail || 'HTTP ' + res.status);
  }
  return res.json();
}

function dateParams(days, start, end) {
  if (start && end) return '&start_date=' + start + '&end_date=' + end;
  return '&days=' + (days || 30);
}

const api = {
  getSites: (params = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.status) q.set('status', params.status);
    return request('/sites' + (q.toString() ? '?' + q.toString() : ''));
  },
  getSite: (id) => request('/sites/' + id),
  createSite: (data) => request('/sites', { method: 'POST', body: JSON.stringify(data) }),
  updateSite: (id, data) => request('/sites/' + id, { method: 'PUT', body: JSON.stringify(data) }),
  getSiteLocaleOptions: () => request('/sites/locale-options'),
  searchLocations: (siteId, q) => request('/sites/' + siteId + '/locations?q=' + encodeURIComponent(q)),
  deleteSite: (id) => request('/sites/' + id, { method: 'DELETE' }),
  checkSiteHealth: (id) => request('/sites/' + id + '/health'),
  getHealth: () => request('/health'),

  getKeywords: (siteId) => request('/sites/' + siteId + '/keywords'),
  addKeywords: (siteId, keywords) => request('/sites/' + siteId + '/keywords', { method: 'POST', body: JSON.stringify({ keywords }) }),
  deleteKeyword: (id) => request('/keywords/' + id, { method: 'DELETE' }),
  bulkUpdateKeywords: (data) => request('/keywords/bulk-update', { method: 'PUT', body: JSON.stringify(data) }),
  bulkDeleteKeywords: (ids) => request('/keywords/bulk-delete?' + ids.map(function(id) { return 'keyword_ids=' + id; }).join('&'), { method: 'DELETE' }),
  getSiteLabels: (siteId) => request('/sites/' + siteId + '/keywords/labels'),
  getRankings: (keywordId, days) => request('/keywords/' + keywordId + '/rankings?days=' + (days || 30)),
  getRankingSummary: (siteId) => request('/sites/' + siteId + '/rankings/summary'),
  triggerRankCheck: (siteId) => request('/sites/' + siteId + '/rankings/check', { method: 'POST' }),
  updateVolumes: (siteId) => request('/sites/' + siteId + '/keywords/update-volumes', { method: 'POST' }),

  // Additional keyphrases (ביטויים נוספים) - separate table, manual check only
  getAdditionalKeywords: (siteId) => request('/sites/' + siteId + '/additional-keywords'),
  addAdditionalKeywords: (siteId, keywords) => request('/sites/' + siteId + '/additional-keywords', { method: 'POST', body: JSON.stringify({ keywords }) }),
  deleteAdditionalKeyword: (id) => request('/additional-keywords/' + id, { method: 'DELETE' }),
  bulkUpdateAdditionalKeywords: (data) => request('/additional-keywords/bulk-update', { method: 'PUT', body: JSON.stringify(data) }),
  bulkDeleteAdditionalKeywords: (ids) => request('/additional-keywords/bulk-delete?' + ids.map(function(id) { return 'keyword_ids=' + id; }).join('&'), { method: 'DELETE' }),
  getAdditionalSiteLabels: (siteId) => request('/sites/' + siteId + '/additional-keywords/labels'),
  getAdditionalRankings: (keywordId, days) => request('/additional-keywords/' + keywordId + '/rankings?days=' + (days || 30)),
  triggerAdditionalRankCheck: (siteId) => request('/sites/' + siteId + '/additional-keywords/check', { method: 'POST' }),
  updateAdditionalVolumes: (siteId) => request('/sites/' + siteId + '/additional-keywords/update-volumes', { method: 'POST' }),

  // Paid advertising (פרסום ממומן)
  getPaidAdsCatalog: () => request('/paid-ads/catalog'),
  getSitePaidAds: (siteId, month) => request('/sites/' + siteId + '/paid-ads/' + month),
  savePaidAds: (siteId, month, payload) => request('/sites/' + siteId + '/paid-ads/' + month, { method: 'PUT', body: JSON.stringify(payload) }),
  getPaidAdsSeries: (siteId) => request('/sites/' + siteId + '/paid-ads/series'),
  addPaidCustomMetric: (siteId, platform, label, type) => request('/sites/' + siteId + '/paid-ads/custom-metrics', { method: 'POST', body: JSON.stringify({ platform: platform, label: label, type: type || 'number' }) }),
  deletePaidCustomMetric: (siteId, platform, key) => request('/sites/' + siteId + '/paid-ads/custom-metrics/' + platform + '/' + key, { method: 'DELETE' }),

  // Portfolio
  getPortfolioSummary: () => request("/portfolio/summary"),
  getPortfolioAlerts: () => request("/portfolio/alerts"),
  // GSC
  getGSCPerformance: (siteId, days = 30) => request(`/sites/${siteId}/gsc/performance?days=${days}`),
  // GA4
  getGA4Summary: (siteId, days = 30) => request(`/sites/${siteId}/ga4/summary?days=${days}`),
  getGA4Daily: (siteId, days = 30) => request(`/sites/${siteId}/ga4/daily?days=${days}`),


  getCosts: (days) => request('/costs?days=' + (days || 30)),

  getGoogleLoginUrl: (siteId) => request('/auth/google/login?site_id=' + siteId),
  getGoogleStatus: (siteId) => request('/auth/google/status/' + siteId),
  disconnectGoogle: (siteId) => request('/auth/google/disconnect/' + siteId, { method: 'DELETE' }),

  getGscProperties: (siteId) => request('/sites/' + siteId + '/google/gsc-properties'),
  getGa4Properties: (siteId) => request('/sites/' + siteId + '/google/ga4-properties'),
  selectProperties: (siteId, data) => request('/sites/' + siteId + '/google/select-properties', { method: 'POST', body: JSON.stringify(data) }),

  // GSC with date support
  getGscKeywords24h: (siteId) => request('/sites/' + siteId + '/gsc/keywords-24h'),
  getGscTopQueries: (siteId, days, limit, start, end) => {
    let url = '/sites/' + siteId + '/gsc/top-queries?limit=' + (limit || 50);
    if (start && end) url += '&start_date=' + start + '&end_date=' + end;
    else url += '&days=' + (days || 30);
    return request(url);
  },

  async getSitesList() {
    return request("/portfolio/sites-list");
  },
  getGscTopPages: (siteId, days, limit, start, end) => {
    let url = '/sites/' + siteId + '/gsc/top-pages?limit=' + (limit || 50);
    if (start && end) url += '&start_date=' + start + '&end_date=' + end;
    else url += '&days=' + (days || 30);
    return request(url);
  },
  getGscDaily: (siteId, days, start, end) => {
    let url = '/sites/' + siteId + '/gsc/daily?';
    if (start && end) url += 'start_date=' + start + '&end_date=' + end;
    else url += 'days=' + (days || 30);
    return request(url);
  },

  // GA4 with date support
  getGa4Summary: (siteId, days, start, end) => {
    let url = '/sites/' + siteId + '/ga4/summary?';
    if (start && end) url += 'start_date=' + start + '&end_date=' + end;
    else url += 'days=' + (days || 30);
    return request(url);
  },
  getGa4Daily: (siteId, days, start, end) => {
    let url = '/sites/' + siteId + '/ga4/daily?';
    if (start && end) url += 'start_date=' + start + '&end_date=' + end;
    else url += 'days=' + (days || 30);
    return request(url);
  },
  getGa4TopPages: (siteId, days, limit, start, end) => {
    let url = '/sites/' + siteId + '/ga4/top-pages?limit=' + (limit || 30);
    if (start && end) url += '&start_date=' + start + '&end_date=' + end;
    else url += '&days=' + (days || 30);
    return request(url);
  },
  getGa4SourcesReport: (siteId, start, end, by) =>
    request('/sites/' + siteId + '/ga4/sources-report?start_date=' + start + '&end_date=' + end + '&by=' + (by || 'channel')),
  getGa4Sources: (siteId, days, start, end) => {
    let url = '/sites/' + siteId + '/ga4/sources?';
    if (start && end) url += 'start_date=' + start + '&end_date=' + end;
    else url += 'days=' + (days || 30);
    return request(url);
  },
};

export default api;

// Scanner & Checklist - added in Phase 1B
api.scanSinglePage = (siteId, url) => request('/sites/' + siteId + '/scan/single?url=' + encodeURIComponent(url), { method: 'POST' });
api.scanFromSitemap = (siteId) => request('/sites/' + siteId + '/scan?from_sitemap=true', { method: 'POST' });
api.getSitemapUrls = (siteId) => request('/sites/' + siteId + '/sitemap-urls');
api.getSiteScans = (siteId) => request('/sites/' + siteId + '/scans');
api.getScanDetail = (scanId) => request('/scans/' + scanId);
api.generateChecklist = (scanId) => request('/scans/' + scanId + '/checklist/generate', { method: 'POST' });
api.getChecklist = (scanId) => request('/scans/' + scanId + '/checklist');
api.updateChecklistItem = (itemId, data) => request('/checklist/' + itemId, { method: 'PUT', body: JSON.stringify(data) });
api.getChecklistSummary = (siteId) => request('/sites/' + siteId + '/checklist/summary');

// Crawl module (Crawly port) - full-site technical scan
api.startCrawl = (siteId, opts = {}) => request('/sites/' + siteId + '/crawl', { method: 'POST', body: JSON.stringify(opts) });
api.getSiteCrawls = (siteId) => request('/sites/' + siteId + '/crawls');
api.getCrawl = (runId) => request('/crawls/' + runId);
api.getCrawlIssues = (runId, params = {}) => {
  const q = new URLSearchParams();
  if (params.severity) q.set('severity', params.severity);
  if (params.category) q.set('category', params.category);
  if (params.status) q.set('status', params.status);
  return request('/crawls/' + runId + '/issues' + (q.toString() ? '?' + q.toString() : ''));
};
api.getCrawlPages = (runId) => request('/crawls/' + runId + '/pages');
api.getCrawlPage = (runId, pageId) => request('/crawls/' + runId + '/pages/' + pageId);
api.deleteCrawl = (runId) => request('/crawls/' + runId, { method: 'DELETE' });
api.crawlIssuesToChecklist = (runId, issueIds) => request('/crawls/' + runId + '/issues/to-checklist', { method: 'POST', body: JSON.stringify({ issue_ids: issueIds }) });
api.cancelCrawl = (runId) => request('/crawls/' + runId + '/cancel', { method: 'POST' });
api.updateCrawlIssueStatus = (runId, issueId, status) => request('/crawls/' + runId + '/issues/' + issueId, { method: 'PATCH', body: JSON.stringify({ status }) });
api.getCrawlImages = (runId) => request('/crawls/' + runId + '/images');
api.getCrawlSchemas = (runId) => request('/crawls/' + runId + '/schemas');
api.generatePageSchema = (runId, pageId, body) => request('/crawls/' + runId + '/pages/' + pageId + '/schema/generate', { method: 'POST', body: JSON.stringify(body) });
api.getPageLinks = (runId, pageId, direction = 'out') => request('/crawls/' + runId + '/pages/' + pageId + '/links?direction=' + direction);
api.getCrawlDeltas = (runId, change) => request('/crawls/' + runId + '/deltas' + (change ? '?change=' + change : ''));
api.getAuditSettings = (siteId) => request('/sites/' + siteId + '/audit-settings');
api.saveAuditSettings = (siteId, data) => request('/sites/' + siteId + '/audit-settings', { method: 'PUT', body: JSON.stringify(data) });

// System Admin - added for management page
api.getScheduledTasks = () => request('/system/tasks');
api.updateScheduledTask = (id, params) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) q.set(k, v); });
  return request('/system/tasks/' + id + '?' + q.toString(), { method: 'PUT' });
};
api.runScheduledTask = (id) => request('/system/tasks/' + id + '/run', { method: 'POST' });
api.getSystemHealth = () => request('/system/health');
api.getEnvironment = () => request('/system/environment');
api.getCostsSummary = () => request('/system/costs/summary');

// Articles - Phase 2A
api.getArticles = (siteId) => request('/articles/' + siteId);
api.getArticle = (articleId) => request('/articles/detail/' + articleId);
api.updateArticle = (articleId, data) => 
  request('/articles/detail/' + articleId, { method: 'PUT', body: JSON.stringify(data) });
api.deleteArticle = (articleId) => request('/articles/detail/' + articleId, { method: 'DELETE' });
api.regenerateArticle = (articleId, instructions) => 
  request('/articles/detail/' + articleId + '/regenerate?instructions=' + encodeURIComponent(instructions || ''), { method: 'POST' });

// Phase 2A - additional features
api.getModels = () => request('/articles/models');
api.cleanArticle = (articleId) => request('/articles/detail/' + articleId + '/clean', { method: 'POST' });
api.factCheckArticle = (articleId) => request('/articles/detail/' + articleId + '/fact-check', { method: 'POST' });
api.generateArticle = (siteId, topic, targetKeyword, model, wordCount) => 
  request('/articles/' + siteId + '/generate?topic=' + encodeURIComponent(topic) + '&target_keyword=' + encodeURIComponent(targetKeyword) + '&model=' + (model || 'sonnet') + '&word_count=' + (wordCount || 1500), { method: 'POST' });

// Fix facts based on fact-check results
api.fixFacts = (articleId, issues) => 
  request('/articles/detail/' + articleId + '/fix-facts', { method: 'POST', body: JSON.stringify(issues) });

// Phase 2B - WordPress publish + taxonomies
api.generateArticleImages = (articleId) =>
  request('/articles/detail/' + articleId + '/generate-images', { method: 'POST' });
api.regenerateArticleImage = (articleId, imageKey, refinementHe) =>
  request('/articles/detail/' + articleId + '/regenerate-image', {
    method: 'POST',
    body: JSON.stringify({ image_key: imageKey, refinement_he: refinementHe || '' }),
  });
api.publishArticle = (articleId, options) =>
  request('/articles/detail/' + articleId + '/publish', {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
api.getWpTaxonomies = (siteId) => request('/articles/' + siteId + '/wp-taxonomies');

// Keyword Research - Phase 2A Dev B
api.runKeywordResearch = (siteId, sources, scDays) =>
  request('/keyword-research/run', { method: 'POST', body: JSON.stringify({ site_id: siteId, sources: sources || ['search_console', 'google_ads'], sc_days_back: scDays || 90 }) });
api.getResearchStatus = (sessionId) => request('/keyword-research/status/' + sessionId);
api.getResearchResults = (sessionId, kwType, priority) => {
  let url = '/keyword-research/results/' + sessionId + '?';
  if (kwType) url += 'keyword_type=' + kwType + '&';
  if (priority) url += 'priority=' + priority;
  return request(url);
};
api.getStrikingDistance = (sessionId, minVolume) =>
  request('/keyword-research/striking-distance/' + sessionId + '?min_volume=' + (minVolume || 0));
api.getResearchSessions = (siteId) => request('/sites/' + siteId + '/keyword-research/sessions');
api.runContentGaps = (siteId, competitors) =>
  request('/keyword-research/content-gaps', { method: 'POST', body: JSON.stringify({ site_id: siteId, competitor_domains: competitors }) });

// Keyword Discovery - מחקר ביטויים מאפס
api.runDiscovery = (data) =>
  request('/keyword-discovery/run', { method: 'POST', body: JSON.stringify(data) });
api.getDiscoveryStatus = (sessionId) => request('/keyword-discovery/status/' + sessionId);
api.getDiscoveryResults = (sessionId) => request('/keyword-discovery/results/' + sessionId);
api.rateDiscoveryKeyword = (sessionId, groupIndex, keywordIndex, rating) =>
  request('/keyword-discovery/' + sessionId + '/rate', { method: 'PUT', body: JSON.stringify({ group_index: groupIndex, keyword_index: keywordIndex, rating: rating }) });
api.selectDiscoveryKeywords = (sessionId, keywords) =>
  request('/keyword-discovery/' + sessionId + '/select', { method: 'POST', body: JSON.stringify({ keywords: keywords }) });
api.getDiscoverySessions = (siteId) => request('/sites/' + siteId + '/keyword-discovery/sessions');

api.enrichIntitle = (sessionId) => request('/keyword-discovery/' + sessionId + '/enrich-intitle', { method: 'POST' });

// Competitors - Phase 3A
api.getCompetitorOverview = (siteId) => request('/competitors/sites/' + siteId + '/overview');
api.getCompetitorAnalysis = (keywordId) => request('/competitors/keywords/' + keywordId);
api.runCompetitorAnalysis = (keywordId, scanPages, fetchAhrefs, runClaude) => {
  let url = '/competitors/keywords/' + keywordId + '/analyze?';
  url += 'scan_pages=' + (scanPages !== false);
  url += '&fetch_ahrefs=' + (fetchAhrefs !== false);
  url += '&run_claude=' + (runClaude !== false);
  return request(url, { method: 'POST' });
};
api.runBulkCompetitorAnalysis = (siteId, keywordIds, maxKeywords) =>
  request('/competitors/sites/' + siteId + '/analyze-bulk', {
    method: 'POST',
    body: JSON.stringify({ keyword_ids: keywordIds || null, max_keywords: maxKeywords || 10 }),
  });
api.getCompetitorHistory = (siteId, limit) => request('/competitors/sites/' + siteId + '/history' + (limit ? '?limit=' + limit : ''));
api.getSerpData = (keywordId) => request('/competitors/serp/' + keywordId);
api.runClaudeGapAnalysis = (keywordId) =>
  request('/competitors/keywords/' + keywordId + '/claude-analysis', { method: 'POST' });

// Tasks - flexible checklist (Phase 3A)
api.createTask = (data) => request('/tasks', { method: 'POST', body: JSON.stringify(data) });
api.createTasksBulk = (tasks) => request('/tasks/bulk', { method: 'POST', body: JSON.stringify({ tasks }) });
api.addMonthlyChecks = (siteId, month) => request('/sites/' + siteId + '/tasks/monthly-checks' + (month ? '?month=' + month : ''), { method: 'POST' });
api.getSiteTasks = (siteId, source, status, priority, month) => {
  let url = '/sites/' + siteId + '/tasks?';
  if (source) url += 'source=' + source + '&';
  if (status) url += 'status=' + status + '&';
  if (priority) url += 'priority=' + priority + '&';
  if (month) url += 'month=' + month;
  return request(url);
};

// Checklist Execute with Claude
api.executeChecklistItem = (itemId, data) => request("/checklist/" + itemId + "/execute", { method: "POST", body: JSON.stringify(data || {}) });
api.publishChecklistItem = (itemId, data) => request("/checklist/" + itemId + "/publish", { method: "POST", body: JSON.stringify(data) });

// Analyze any keyword (ad-hoc, not just tracked)
api.analyzeCustomKeyword = (siteId, keyword, scanPages, fetchAhrefs, runClaude) => {
  let url = '/competitors/sites/' + siteId + '/analyze-keyword?keyword=' + encodeURIComponent(keyword);
  url += '&scan_pages=' + (scanPages !== false);
  url += '&fetch_ahrefs=' + (fetchAhrefs !== false);
  url += '&run_claude=' + (runClaude !== false);
  return request(url, { method: 'POST' });
};

// ── GEO Module (Phase 4) ──
api.runGeoAudit = (data) => request('/geo/audit', { method: 'POST', body: JSON.stringify(data) });
api.getGeoAudit = (id) => request('/geo/audit/' + id);
api.getSiteGeoAudits = (siteId) => request('/geo/sites/' + siteId + '/audits');
api.getGeoStatus = (siteId) => request('/geo/sites/' + siteId + '/status');
api.previewSchema = (url, types) => {
  var q = 'url=' + encodeURIComponent(url);
  if (types) q += '&types=' + types;
  return request('/geo/schema/preview?' + q);
};
api.injectSchema = (data) => request('/geo/schema/inject', { method: 'POST', body: JSON.stringify(data) });
api.optimizeContent = (data) => request('/geo/optimize', { method: 'POST', body: JSON.stringify(data) });
api.getLlmsTxtStatus = (siteId) => request('/geo/llms-txt/status/' + siteId);
api.generateLlmsTxt = (siteId) => request('/geo/llms-txt/generate/' + siteId, { method: 'POST' });
api.uploadLlmsTxt = (siteId) => request('/geo/llms-txt/upload/' + siteId, { method: 'POST' });
api.checkCitations = (data) => request('/geo/citations/check', { method: 'POST', body: JSON.stringify(data) });
api.getCitationHistory = (siteId, params) => {
  var q = new URLSearchParams();
  if (params && params.days) q.set('days', params.days);
  if (params && params.platform) q.set('platform', params.platform);
  if (params && params.cited_only) q.set('cited_only', 'true');
  var qs = q.toString();
  return request('/geo/sites/' + siteId + '/citations' + (qs ? '?' + qs : ''));
};
api.suggestCitationQueries = (siteId) => request('/geo/citations/suggest-queries', { method: 'POST', body: JSON.stringify({ site_id: siteId }) });

// ── AI Citation Monitor V2 — DataForSEO LLM Responses / LLM Mentions ──
api.checkLlmVisibility = (data) => request('/geo/citations/check-llm', { method: 'POST', body: JSON.stringify(data) });
api.checkLlmMentions = (data) => request('/geo/citations/check-mentions', { method: 'POST', body: JSON.stringify(data) });
api.getLlmVisibilityHistory = (siteId, params) => {
  var q = new URLSearchParams();
  if (params && params.days) q.set('days', params.days);
  if (params && params.platform) q.set('platform', params.platform);
  if (params && params.cited_only) q.set('cited_only', 'true');
  var qs = q.toString();
  return request('/geo/sites/' + siteId + '/citations-all' + (qs ? '?' + qs : ''));
};
api.verifyMentionsCoverage = () => request('/geo/verify-mentions-coverage');

// ── GEO measurement — fixed prompt set, weekly runs ──
api.getGeoProject = (siteId) => request('/geo-measurement/sites/' + siteId + '/project');
api.updateGeoProject = (siteId, data) => request('/geo-measurement/sites/' + siteId + '/project', { method: 'PUT', body: JSON.stringify(data) });
api.getGeoPrompts = (siteId) => request('/geo-measurement/sites/' + siteId + '/prompts');
api.addGeoPrompts = (siteId, prompts) => request('/geo-measurement/sites/' + siteId + '/prompts', { method: 'POST', body: JSON.stringify({ prompts: prompts }) });
api.suggestGeoPrompts = (siteId, count) => request('/geo-measurement/sites/' + siteId + '/prompts/suggest', { method: 'POST', body: JSON.stringify({ count: count }) });
api.updateGeoPrompt = (promptId, data) => request('/geo-measurement/prompts/' + promptId, { method: 'PUT', body: JSON.stringify(data) });
api.retireGeoPrompt = (promptId) => request('/geo-measurement/prompts/' + promptId, { method: 'DELETE' });
api.purgeGeoPrompt = (promptId) => request('/geo-measurement/prompts/' + promptId + '/purge', { method: 'DELETE' });
api.runGeoMeasurement =(siteId) => request('/geo-measurement/sites/' + siteId + '/run', { method: 'POST' });
function rangeQuery(range) {
  const q = new URLSearchParams();
  if (range && range.start) q.set('start_date', range.start);
  if (range && range.end) q.set('end_date', range.end);
  return q;
}
api.getGeoResults = (siteId, runs, range) => {
  const q = rangeQuery(range);
  if (runs) q.set('runs', runs);
  return request('/geo-measurement/sites/' + siteId + '/results' + (q.toString() ? '?' + q : ''));
};
api.getGeoCheck = (checkId) => request('/geo-measurement/checks/' + checkId);
api.backfillGeoMetrics = (siteId, days) => request('/geo-measurement/sites/' + siteId + '/backfill' + (days ? '?days=' + days : ''), { method: 'POST' });
api.getGeoReport = (siteId, range) => {
  const q = rangeQuery(range);
  return request('/geo-measurement/sites/' + siteId + '/report' + (q.toString() ? '?' + q : ''));
};
api.getGeoMetrics = (siteId, days) => request('/geo-measurement/sites/' + siteId + '/metrics' + (days ? '?days=' + days : ''));
api.getGeoOverview = () => request('/geo-measurement/overview');
api.generateGeoSuggestions = (siteIds, count) => request('/geo-measurement/suggestions/generate', { method: 'POST', body: JSON.stringify({ site_ids: siteIds, count: count }) });
api.getGeoSuggestions = (siteId) => request('/geo-measurement/sites/' + siteId + '/suggestions');
api.approveGeoSuggestions = (siteId, items) => request('/geo-measurement/sites/' + siteId + '/suggestions/approve', { method: 'POST', body: JSON.stringify({ items: items }) });
api.dismissGeoSuggestions = (siteId, ids) => request('/geo-measurement/sites/' + siteId + '/suggestions/dismiss', { method: 'POST', body: JSON.stringify({ ids: ids }) });

// ── Client Success Stories (Phase 2B - v2) ──
api.generateSuccessStory = (data) =>
  request('/success-stories/generate', { method: 'POST', body: JSON.stringify(data) });
api.listSuccessStories = (siteId) =>
  request('/success-stories/site/' + siteId);
api.getSuccessStory = (id) => request('/success-stories/' + id);
api.updateSuccessStory = (id, data) =>
  request('/success-stories/' + id, { method: 'PUT', body: JSON.stringify(data) });
api.deleteSuccessStory = (id) =>
  request('/success-stories/' + id, { method: 'DELETE' });
api.regenerateSuccessStory = (id, data) =>
  request('/success-stories/' + id + '/regenerate', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
api.replaceStoryDetail = (id, inText, newValue) =>
  request('/success-stories/' + id + '/replace-detail', {
    method: 'POST',
    body: JSON.stringify({ in_text: inText, new_value: newValue }),
  });
api.generateStoryImages = (id) =>
  request('/success-stories/' + id + '/generate-images', { method: 'POST' });
api.regenerateStoryImage = (id, imageKey, refinementHe) =>
  request('/success-stories/' + id + '/regenerate-image', {
    method: 'POST',
    body: JSON.stringify({ image_key: imageKey, refinement_he: refinementHe || '' }),
  });
api.publishStoryToWp = (id, options) =>
  request('/success-stories/' + id + '/publish-to-wp', {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
api.listStoryWpPostTypes = (siteId) =>
  request('/success-stories/site/' + siteId + '/wp-post-types');
api.inspectStoryCptFields = (siteId, restBase) =>
  request('/success-stories/site/' + siteId + '/cpt/' + restBase + '/fields');

// ── Links / Backlinks module ──
api.getBacklinks = (siteId) => request('/links/sites/' + siteId + '/backlinks');
api.createBacklink = (siteId, data) =>
  request('/links/sites/' + siteId + '/backlinks', { method: 'POST', body: JSON.stringify(data) });
api.updateBacklink = (id, data) =>
  request('/links/backlinks/' + id, { method: 'PUT', body: JSON.stringify(data) });
api.deleteBacklink = (id) => request('/links/backlinks/' + id, { method: 'DELETE' });
api.checkBacklink = (id) => request('/links/backlinks/' + id + '/check', { method: 'POST' });

api.getLinkContacts = () => request('/links/contacts');
api.createLinkContact = (data) =>
  request('/links/contacts', { method: 'POST', body: JSON.stringify(data) });
api.updateLinkContact = (id, data) =>
  request('/links/contacts/' + id, { method: 'PUT', body: JSON.stringify(data) });
api.deleteLinkContact = (id) => request('/links/contacts/' + id, { method: 'DELETE' });

// Zefo import: multipart upload (bypasses the JSON `request` helper so the
// browser sets the multipart boundary itself).
function uploadLinksImport(path, linksFile, contactsFile) {
  const fd = new FormData();
  if (linksFile) fd.append('links', linksFile);
  if (contactsFile) fd.append('contacts', contactsFile);
  return fetch(API_BASE + path, { method: 'POST', body: fd }).then(async (res) => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(err.detail || 'HTTP ' + res.status);
    }
    return res.json();
  });
}
api.analyzeLinksImport = (linksFile, contactsFile) =>
  uploadLinksImport('/links/import/analyze', linksFile, contactsFile);
api.commitLinksImport = (linksFile, contactsFile) =>
  uploadLinksImport('/links/import/commit', linksFile, contactsFile);
api.getStoryStylePrompt = (siteId) =>
  request('/success-stories/site/' + siteId + '/style-prompt');
api.updateStoryStylePrompt = (siteId, prompt) =>
  request('/success-stories/site/' + siteId + '/style-prompt', {
    method: 'PUT',
    body: JSON.stringify({ client_story_style_prompt: prompt }),
  });

// ── Monthly Plans (Phase 3A) ──
api.getMonthlyPlans = (siteId) => request('/sites/' + siteId + '/monthly-plans');
api.getMonthlyPlan = (siteId, month) => request('/sites/' + siteId + '/monthly-plans/' + month);
api.generateMonthlyPlan = (siteId, month) => request('/sites/' + siteId + '/monthly-plans/generate' + (month ? '?month=' + month : ''), { method: 'POST' });
api.updateMonthlyPlan = (siteId, month, data) => request('/sites/' + siteId + '/monthly-plans/' + month, { method: 'PATCH', body: JSON.stringify(data) });
api.markPlanSent = (siteId, month) => request('/sites/' + siteId + '/monthly-plans/' + month + '/mark-sent', { method: 'PATCH' });

// Rank Check Schedule
api.getRankSchedule = () => request('/system/rank-schedule');
api.updateRankSchedule = (days) => request('/system/rank-schedule', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ days: days }),
});
api.updateSiteRankSchedule = (siteId, mode, days) => request('/system/sites/' + siteId + '/rank-schedule', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ mode: mode, days: days }),
});
api.getRankDepth = () => request('/system/rank-depth');
api.updateRankDepth = (depth) => request('/system/rank-depth', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ depth: depth }),
});

// Weekly summary email recipients
api.getWeeklyRecipients = () => request('/system/weekly-summary-recipients');
api.updateWeeklyRecipients = (emails) => request('/system/weekly-summary-recipients', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ emails: emails }),
});
api.runWeeklySummary = () => request('/system/weekly-summary/run', { method: 'POST' });

// Configurable alert rules
api.getAlertRules = () => request('/system/alert-rules');
api.createAlertRule = (data) => request('/system/alert-rules', { method: 'POST', body: JSON.stringify(data) });
api.updateAlertRule = (id, data) => request('/system/alert-rules/' + id, { method: 'PUT', body: JSON.stringify(data) });
api.deleteAlertRule = (id) => request('/system/alert-rules/' + id, { method: 'DELETE' });
api.runAlertRules = () => request('/system/alerts/run', { method: 'POST' });

// Checklist upgrade - report items + delete + team
api.getReportItems = (siteId, month) => { let url = '/sites/' + siteId + '/tasks/report-items'; if (month) url += '?month=' + month; return request(url); };
api.deleteChecklistItem = (itemId) => request('/checklist/' + itemId, { method: 'DELETE' });
api.getTeamMembers = () => request('/team');

// Reports - Phase 3B
api.getReportsOverview = () => request('/reports/overview');
api.getReports = (siteId) => request('/reports/sites/' + siteId + '?limit=50');
api.getReport = (siteId, month) => request('/reports/sites/' + siteId + '/' + month);
api.getReportPreview = (siteId, month) => request('/reports/sites/' + siteId + '/' + month + '/preview');
api.generateReport = (siteId, month) => request('/reports/sites/' + siteId + '/generate' + (month ? '?month=' + month : ''), { method: 'POST' });
api.generateReportAsync = (siteId, month) => request('/reports/sites/' + siteId + '/generate-async' + (month ? '?month=' + month : ''), { method: 'POST' });
api.sendReport = (reportId, email, subject, message, signatureId) => request('/reports/' + reportId + '/send', { method: 'POST', body: JSON.stringify({ email: email, subject: subject || null, message: message || null, signature_id: signatureId || null }) });
api.deleteReport = (reportId) => request('/reports/' + reportId, { method: 'DELETE' });
api.saveReportSummary = (reportId, html) => request('/reports/' + reportId + '/summary', { method: 'PUT', body: JSON.stringify({ manual_summary: html || '' }) });
api.saveReportSummaryByMonth = (siteId, month, html) => request('/reports/sites/' + siteId + '/' + month + '/summary', { method: 'PUT', body: JSON.stringify({ manual_summary: html || '' }) });
api.saveReportConfig = (siteId, month, config) => request('/reports/sites/' + siteId + '/' + month + '/config', { method: 'PUT', body: JSON.stringify(config) });
api.getPortalLink = (siteId) => request('/reports/sites/' + siteId + '/portal-link');
api.getReportSendDefaults = (siteId) => request('/reports/sites/' + siteId + '/send-defaults');

// Monthly client summary email (computed from business results, phrased by the LLM)
api.getMonthlySummary = (siteId, month) => request('/sites/' + siteId + '/monthly-summary/' + month);
api.generateMonthlySummary = (siteId, month, payload) => request('/sites/' + siteId + '/monthly-summary/' + month + '/generate', { method: 'POST', body: JSON.stringify(payload) });
api.saveMonthlySummary = (siteId, month, text) => request('/sites/' + siteId + '/monthly-summary/' + month, { method: 'PUT', body: JSON.stringify({ client_report: text || '' }) });
api.saveMonthlySummaryConfig = (siteId, payload) => request('/sites/' + siteId + '/monthly-summary/config', { method: 'PUT', body: JSON.stringify(payload) });

// Uptime / availability board (standalone - unrelated to client reports)
api.getUptimeOverview = (days) => request('/uptime/overview?days=' + (days || 30));
api.getSiteIncidents = (siteId, days) => request('/uptime/sites/' + siteId + '/incidents?days=' + (days || 90));

// Email signatures (global)
api.getSignatures = () => request('/signatures');
api.createSignature = (data) => request('/signatures', { method: 'POST', body: JSON.stringify(data) });
api.updateSignature = (id, data) => request('/signatures/' + id, { method: 'PUT', body: JSON.stringify(data) });
api.deleteSignature = (id) => request('/signatures/' + id, { method: 'DELETE' });
api.setDefaultSignature = (id) => request('/signatures/' + id + '/set-default', { method: 'POST' });
api.uploadSignatureImage = (dataUrl) => request('/signatures/upload-image', { method: 'POST', body: JSON.stringify({ image: dataUrl }) });
